<template>
  <section class="section section-generator">
    <!-- 侧边栏菜单 -->
    <div class="sidebar" :class="{ 'sidebar-expanded': isSidebarExpanded }">
      <!-- 收缩状态 -->
      <div class="sidebar-collapsed" v-if="!isSidebarExpanded">
        <button class="menu-toggle" @click="toggleSidebar">
          <i class="icon">☰</i>
        </button>
        <div class="collapsed-icons">
          <button class="icon-btn" title="首页" @click="navigateTo('/')">
            <i class="icon">🏠</i>
          </button>
          <button class="icon-btn" title="文案创作" @click="navigateTo('/text-generator-pro')" :class="{ 'active': currentRoute === '/text-generator-pro' }">
            <i class="icon">📄</i>
          </button>
          <button class="icon-btn" title="图像创作" @click="navigateTo('/image-generator')" :class="{ 'active': currentRoute === '/image-generator' }">
            <i class="icon">🖼️</i>
          </button>
          <button class="icon-btn" title="视频创作" @click="navigateTo('/video-generator-pro')" :class="{ 'active': currentRoute === '/video-generator-pro' }">
            <i class="icon">🎥</i>
          </button>
          <button class="icon-btn" title="短剧创作" @click="navigateTo('/drama-generator-pro')" :class="{ 'active': currentRoute === '/drama-generator-pro' || currentRoute === '/drama' }">
            <i class="icon">🎭</i>
          </button>
        </div>
      </div>
      
      <!-- 展开状态 -->
      <div class="sidebar-expanded-content" v-else>
        <div class="sidebar-header">
          <div class="logo">
            <div class="logo-icon">
              <img :src="logoUrl" alt="Logo" class="logo-img" />
            </div>
            <span class="logo-text">{{ siteTitle }}</span>
          </div>
          <button class="back-toggle" @click="toggleSidebar">
            <i class="icon">←</i>
          </button>
        </div>
        <div class="sidebar-menu">
          <button class="menu-item" @click="navigateTo('/')">
            <i class="icon">🏠</i>
            <span>首页</span>
          </button>
          <button class="menu-item" @click="navigateTo('/text-generator-pro')" :class="{ 'active': currentRoute === '/text-generator-pro' }">
            <i class="icon">📄</i>
            <span>文案创作</span>
          </button>
          <button class="menu-item" @click="navigateTo('/image-generator')" :class="{ 'active': currentRoute === '/image-generator' }">
            <i class="icon">🖼️</i>
            <span>图像创作</span>
          </button>
          <button class="menu-item" @click="navigateTo('/video-generator-pro')" :class="{ 'active': currentRoute === '/video-generator-pro' }">
            <i class="icon">🎥</i>
            <span>视频创作</span>
          </button>
          <button class="menu-item" @click="navigateTo('/drama-generator-pro')" :class="{ 'active': currentRoute === '/drama-generator-pro' || currentRoute === '/drama' }">
            <i class="icon">🎭</i>
            <span>短剧创作</span>
          </button>
        </div>
      </div>
    </div>
    
    <div class="main-content" :class="{ 'main-content-expanded': isSidebarExpanded }">
      <!-- 动态内容区域 -->
      <div class="content-area">
        <transition name="fade" mode="out-in">
          <component :is="currentComponent" :key="currentRoute" :params="routeParams" ref="contentComponent"></component>
        </transition>
      </div>
      
      <!-- 生成工具栏 -->
      <div class="generate-toolbar" :class="{ collapsed: isToolbarCollapsed }">
        <!-- 收缩/展开按钮 -->
        <div class="toolbar-toggle" @click="toggleToolbar">
          <div class="toggle-arrow" :class="{ up: isToolbarCollapsed }">
            {{ isToolbarCollapsed ? '▲' : '▼' }}
          </div>
        </div>
        
        <div class="toolbar-content">
          <component 
            :is="currentToolbar" 
            :key="currentRoute + '-toolbar'" 
            :params="routeParams" 
            ref="toolbarComponent"
            @add-drama="handleAddDrama"
            @add-video="handleAddVideo"
            @update-video-progress="handleUpdateVideoProgress"
            @update-video-status="handleUpdateVideoStatus"
            @add-text="handleAddText"
            @add-image="handleAddImage"
            @update-progress="handleUpdateProgress"
            @update-result="handleUpdateResult"
            @retry-image="handleRetryImage"
            @video-generated="handleVideoGenerated"
          ></component>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import VideoContent from './content/VideoContent.vue'
import TextContent from './content/TextContent.vue'
import ImageContent from './content/ImageContent.vue'
import DramaContent from './content/DramaContent.vue'
import VideoToolbar from './toolbar/VideoToolbar.vue'
import TextToolbar from './toolbar/TextToolbar.vue'
import ImageToolbar from './toolbar/ImageToolbar.vue'
import DramaToolbar from './toolbar/DramaToolbar.vue'
import { authMixin } from '../utils/authMixin'

export default {
  name: 'GeneratorPro',
  mixins: [authMixin],
  components: {
    VideoContent,
    TextContent,
    ImageContent,
    DramaContent,
    VideoToolbar,
    TextToolbar,
    ImageToolbar,
    DramaToolbar
  },
  data() {
    return {
      isSidebarExpanded: false,
      isToolbarCollapsed: false,
      currentRoute: '/video-generator-pro',
      routeParams: {},
      siteTitle: '韦小布AI',
      logoUrl: '/images/logo.jpg'
    }
  },
  computed: {
    currentComponent() {
      switch(this.currentRoute) {
        case '/text-generator-pro':
          return 'TextContent'
        case '/image-generator':
          return 'ImageContent'
        case '/drama-generator-pro':
        case '/drama':
          return 'DramaContent'
        default:
          return 'VideoContent'
      }
    },
    currentToolbar() {
      switch(this.currentRoute) {
        case '/text-generator-pro':
          return 'TextToolbar'
        case '/image-generator':
          return 'ImageToolbar'
        case '/drama-generator-pro':
        case '/drama':
          return 'DramaToolbar'
        default:
          return 'VideoToolbar'
      }
    }
  },
  watch: {
    '$route.path': {
      handler(newPath) {
        this.currentRoute = newPath
      },
      immediate: true
    },
    '$route.query': {
      handler(newQuery) {
        if (Object.keys(newQuery).length > 0) {
          this.routeParams = { ...newQuery }
          // 展开工具栏
          this.isToolbarCollapsed = false
        }
      },
      immediate: true
    }
  },
  mounted() {
    this.currentRoute = this.$route.path
    if (Object.keys(this.$route.query).length > 0) {
      this.routeParams = { ...this.$route.query }
      this.isToolbarCollapsed = false
    }
    this.loadSiteSettings()
  },
  methods: {
    async loadSiteSettings() {
      try {
        const response = await this.$axios.get('/admin/site-basic')
        if (response.data && response.data.code === 200) {
          this.siteTitle = response.data.data.siteTitle || '韦小布AI'
          const logoImage = response.data.data.logoImage
          if (logoImage && logoImage.trim() !== '') {
            this.logoUrl = logoImage
          }
        }
      } catch (error) {
        console.error('加载网站设置失败:', error)
      }
    },
    toggleSidebar() {
      this.isSidebarExpanded = !this.isSidebarExpanded
    },
    toggleToolbar() {
      this.isToolbarCollapsed = !this.isToolbarCollapsed
    },
    navigateTo(path) {
      if (this.$route.path !== path) {
        this.$router.push(path)
        this.currentRoute = path
      }
    },
    
    handleAddDrama(newDrama) {
      if (this.$refs.contentComponent && this.$refs.contentComponent.addNewDrama) {
        this.$refs.contentComponent.addNewDrama(newDrama)
      }
    },
    
    handleAddVideo(newVideo) {
      if (this.$refs.contentComponent && this.$refs.contentComponent.addNewVideo) {
        this.$refs.contentComponent.addNewVideo(newVideo)
      }
    },
    
    handleUpdateVideoProgress(data) {
      if (this.$refs.contentComponent && this.$refs.contentComponent.updateVideoProgress) {
        this.$refs.contentComponent.updateVideoProgress(data)
      }
    },
    
    handleUpdateVideoStatus(data) {
      if (this.$refs.contentComponent && this.$refs.contentComponent.updateVideoStatus) {
        this.$refs.contentComponent.updateVideoStatus(data)
      }
    },
    
    handleAddText(newText) {
      if (this.$refs.contentComponent && this.$refs.contentComponent.addNewText) {
        this.$refs.contentComponent.addNewText(newText)
      }
    },
    
    handleAddImage(newImage) {
      console.log('=== GeneratorPro: handleAddImage ===')
      console.log('newImage:', newImage)
      if (this.$refs.contentComponent && this.$refs.contentComponent.addNewImage) {
        console.log('调用 contentComponent.addNewImage')
        this.$refs.contentComponent.addNewImage(newImage)
      }
    },
    
    handleUpdateProgress(index, progress) {
      console.log('=== GeneratorPro: handleUpdateProgress ===')
      console.log('index:', index, 'progress:', progress)
      if (this.$refs.contentComponent && this.$refs.contentComponent.updateImageProgress) {
        console.log('调用 contentComponent.updateImageProgress')
        this.$refs.contentComponent.updateImageProgress(index, progress)
      }
    },
    
    handleUpdateResult(index, imageUrl) {
      console.log('=== GeneratorPro: handleUpdateResult ===')
      console.log('index:', index, 'imageUrl:', imageUrl)
      if (this.$refs.contentComponent && this.$refs.contentComponent.updateImageResult) {
        console.log('调用 contentComponent.updateImageResult')
        this.$refs.contentComponent.updateImageResult(index, imageUrl)
      }
    },
    
    handleRetryImage(item) {
      console.log('=== GeneratorPro: handleRetryImage ===')
      console.log('item:', item)
      // 将图片参数传递给工具栏进行重新生成
      if (this.$refs.toolbarComponent && this.$refs.toolbarComponent.retryFromItem) {
        this.$refs.toolbarComponent.retryFromItem(item)
      }
    },
    
    handleVideoGenerated(videoData) {
      console.log('=== GeneratorPro: handleVideoGenerated ===')
      console.log('videoData:', videoData)
      // 更新生成中视频的结果
      if (this.$refs.contentComponent && this.$refs.contentComponent.updateVideoResult) {
        console.log('调用 contentComponent.updateVideoResult')
        this.$refs.contentComponent.updateVideoResult(videoData)
      }
    }
  }
}
</script>

<style scoped>
.section-generator {
  min-height: 100vh;
  background: #0d0f16;
  color: #e6f1ff;
  position: relative;
  overflow: hidden;
  padding: 0;
  margin: 0;
}

/* 侧边栏样式 */
.sidebar {
  position: fixed;
  left: 0;
  top: 0;
  height: 100vh;
  width: 60px;
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(10px);
  transition: width 0.3s ease;
  z-index: 1000;
}

.sidebar-expanded {
  width: 240px;
}

.sidebar-collapsed {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 0;
  height: 100%;
}

.menu-toggle {
  background: none;
  border: none;
  color: #e6f1ff;
  font-size: 24px;
  cursor: pointer;
  margin-bottom: 30px;
  padding: 10px;
  border-radius: 8px;
  transition: background 0.3s ease;
}

.menu-toggle:hover {
  background: rgba(255, 255, 255, 0.1);
}

.collapsed-icons {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.icon-btn {
  background: none;
  border: none;
  color: #00e6ac;
  font-size: 24px;
  cursor: pointer;
  padding: 12px;
  border-radius: 8px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
}

.icon-btn:hover {
  color: #00e6ac;
  background: rgba(0, 230, 172, 0.1);
  transform: translateY(-2px);
}

.icon-btn.active {
  color: #00e6ac;
  background: rgba(0, 230, 172, 0.1);
}

.sidebar-expanded-content {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 20px;
}

.sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 40px;
}

.logo {
  display: flex;
  align-items: center;
  gap: 12px;
}

.logo-icon {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  overflow: hidden;
}

.logo-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.logo-text {
  font-size: 18px;
  font-weight: bold;
  color: #e6f1ff;
}

.back-toggle {
  background: none;
  border: none;
  color: #e6f1ff;
  font-size: 20px;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  transition: background 0.3s ease;
}

.back-toggle:hover {
  background: rgba(255, 255, 255, 0.1);
}

.sidebar-menu {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.menu-item {
  display: flex;
  align-items: center;
  gap: 12px;
  background: none;
  border: none;
  color: #8892b0;
  font-size: 16px;
  cursor: pointer;
  padding: 12px 16px;
  border-radius: 8px;
  transition: all 0.3s ease;
  text-align: left;
}

.menu-item:hover {
  color: #e6f1ff;
  background: rgba(255, 255, 255, 0.1);
  transform: translateX(4px);
}

.menu-item.active {
  color: #00e6ac;
  background: rgba(0, 230, 172, 0.1);
}

.menu-item .icon {
  font-size: 20px;
  width: 24px;
  text-align: center;
}

/* 主内容区 */
.main-content {
  margin-left: 60px;
  min-height: 100vh;
  transition: margin-left 0.3s ease;
  position: relative;
  padding: 0;
  display: flex;
  flex-direction: column;
  background: #0d0f16;
}

.main-content-expanded {
  margin-left: 240px;
}

/* 内容区域 */
.content-area {
  flex: 1;
  margin-bottom: 180px;
  padding: 0;
}

/* 生成工具栏 */
.generate-toolbar {
  position: fixed;
  bottom: 0;
  left: 60px;
  right: 0;
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(10px);
  padding: 20px;
  transition: all 0.3s ease;
  z-index: 999;
  max-height: 80vh;
  overflow-y: auto;
  overflow-x: hidden;
}

.generate-toolbar.collapsed {
  height: 50px;
  padding: 10px 20px;
  left: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(10px);
  border-radius: 16px 16px 0 0;
}

.main-content-expanded + .generate-toolbar {
  left: 240px;
}

.main-content-expanded + .generate-toolbar.collapsed {
  left: 0;
}

.toolbar-toggle {
  position: absolute;
  top: -25px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(10px);
  border: none;
  border-bottom: none;
  border-radius: 8px 8px 0 0;
  width: 50px;
  height: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 1000;
}

.toolbar-toggle:hover {
  background: rgba(255, 255, 255, 0.06);
}

.toggle-arrow {
  color: #00e6ac;
  font-size: 16px;
  animation: pulse 2s infinite;
  transition: transform 0.3s ease;
}

.toggle-arrow.up {
  transform: rotate(180deg);
}

@keyframes pulse {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-2px);
  }
}

.toolbar-content {
  transition: opacity 0.3s ease;
  position: relative;
  z-index: 1000;
}

.generate-toolbar.collapsed .toolbar-content {
  opacity: 0;
  pointer-events: none;
}

/* 过渡动画 */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
    padding: 20px 16px;
  }
  
  .main-content-expanded {
    margin-left: 0;
  }
  
  .sidebar {
    transform: translateX(-100%);
  }
  
  .sidebar-expanded {
    transform: translateX(0);
    width: 240px;
  }
  
  .generate-toolbar {
    left: 0;
    right: 0;
  }
  
  .content-area {
    margin-bottom: 200px;
  }
}
</style>